// Redux slice and async thunks for session and authentication state
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { type AuthTokenData, type SessionState } from '../types/session.types';
import sessionAPI from '../api/sessionAPI';
import appLocalStorage from './appLocalStorage';
import { Roles } from '@/utils/enums';
import { isTokenExpired } from '../utils/helpers';
import authorizedAPIInstance from '@/api/authorizedAPI';

// Initial state for session slice
const initialState: SessionState = {
  user: null,
  loading: false,
  isAuthorized: false,
  isAppInitialized: false,
  role: Roles.UNKNOWN,
  siteCode: null,
  responseErrorStatus: 0,
};

// Async thunk for sign-in flow
export const signIn = createAsyncThunk(
  'session/signIn',
  (_, { rejectWithValue }) => {
    try {
      console.log(
        'Initiating sign-in process',
        import.meta.env.VITE_APP_AUTH_URL
      );
      return sessionAPI.signIn();
    } catch (error) {
      const { message } = error as Error;
      return rejectWithValue(message);
    }
  }
);

// Async thunk for beginning a session (token exchange and user info)
export const beginSession = createAsyncThunk<
  any,
  string | null,
  { rejectValue: string }
>('session/beginSession', async (code: string | null, { rejectWithValue }) => {
  try {
    const tokenData = appLocalStorage.getTokenData();
    let storedIdToken: any = null;
    if (tokenData?.id_token) {
      storedIdToken = jwtDecode(tokenData?.id_token);
      if (isTokenExpired(storedIdToken)) {
        const tokenResponse = await sessionAPI.refreshAccessToken(
          tokenData?.refresh_token
        );
        const newTokenData = {
          ...tokenResponse?.data,
          refresh_token: tokenData?.refresh_token,
        };
        storedIdToken = jwtDecode(newTokenData?.id_token);
        appLocalStorage.setTokenData(newTokenData);
      }
      return {
        user: {
          email: storedIdToken.email,
          givenName: storedIdToken.given_name,
          familyName: storedIdToken.family_name,
        },
        expTime: storedIdToken.exp,
      };
    }
    if (!code) {
      return { user: null, expTime: null };
    }
    const tokenDataResponse: AxiosResponse<AuthTokenData> =
      await sessionAPI.getAuthToken(code);
    appLocalStorage.setTokenData(tokenDataResponse.data);
    const idToken: any = jwtDecode(tokenDataResponse.data.id_token);
    return {
      user: {
        email: idToken.email,
        givenName: idToken.given_name,
        familyName: idToken.family_name,
      },
      expTime: idToken.exp,
    };
  } catch (error) {
    const { message } = error as Error;
    appLocalStorage.clearLocalStorage();
    return rejectWithValue(message);
  }
});

export const validateUser = createAsyncThunk(
  'session/validateUser',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await authorizedAPIInstance.get<any>('/api/validate', {
        _skipAuthRedirect: true,
      } as any);
      return data;
    } catch (error) {
      const status = (error as any)?.response?.status ?? 0;
      appLocalStorage.clearLocalStorage();
      return rejectWithValue(status);
    }
  }
);

// Session slice with reducers for authentication, user, and loading state
export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    signOut: state => {
      state.user = null;
      state.role = Roles.UNKNOWN;
      state.siteCode = null;
      appLocalStorage.clearLocalStorage();
      state.isAuthorized = false;
      state.isAppInitialized = true;
    },
    // Marks app as authenticated
    setIsAuthorized: state => {
      state.isAuthorized = true;
      state.isAppInitialized = true;
    },
    // Clears user data and local storage
    clearUserData: state => {
      state.user = null;
      appLocalStorage.clearLocalStorage();
    },
    // Sets user info in state
    setUser: (state, action) => {
      state.user = action.payload;
      state.loading = false;
    },
    updateUser: (state, action) => {
      state.user = { ...(state.user || {}), ...action.payload };
    },
    // Sets loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setResponseErrorStatus: (state, action) => {
      state.responseErrorStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Handles beginSession async thunk states
      .addCase(beginSession.pending, state => {
        state.loading = true;
      })
      .addCase(beginSession.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
      })
      .addCase(beginSession.rejected, state => {
        state.loading = false;
        state.user = null;
        state.isAuthorized = false;
        state.isAppInitialized = true;
      })
      .addCase(validateUser.fulfilled, (state, action) => {
        state.role = action.payload?.role as Roles;
        state.siteCode = action.payload?.plants;
      })
      .addCase(validateUser.rejected, (state, action) => {
        state.responseErrorStatus = (action.payload as number) || 401;
        state.isAppInitialized = true;
        state.loading = false;
        state.user = null;
        state.isAuthorized = false;
      });
  },
});

export const {
  setIsAuthorized,
  clearUserData,
  setUser,
  signOut,
  setLoading,
  setResponseErrorStatus,
  updateUser,
} = sessionSlice.actions;

export default sessionSlice.reducer;
